import React, { useState } from 'react';
import {
  BarList, Btn, DataTable, KPI, KPIRow, Loading, Msg, Section, Select,
  SubTabs, panelPost, useAdminGet,
} from './kit';

/**
 * PostHog product analytics, read through the backend.
 *
 * The browser's PostHog key is write-only, so every read here goes through
 * /admin/panel/posthog/*, which uses a server-side Personal API key. If that
 * key isn't set the handlers answer `configured: false` with a reason, and we
 * show the setup note instead of an error.
 *
 * One quirk worth knowing while reading this file: the frontend records
 * everything as a single PostHog event (`utech_click`) with an `action`
 * property. So "which thing did the user do" is a property, and every table
 * below is grouped by action rather than by event name.
 */

const TABS = ['Overview', 'Actions', 'Funnels', 'People', 'Query'];
const RANGES = ['7', '14', '30', '90', '180'];

const PostHogPanel: React.FC = () => {
  const [tab, setTab] = useState(TABS[0]);
  const [days, setDays] = useState('30');
  const config = useAdminGet('posthog', 'config');

  if (config.loading) return <Loading label="Checking PostHog configuration…" />;

  if (config.data && config.data.configured === false) {
    return (
      <div className="flex flex-col gap-4">
        <Msg kind="error">{config.data.reason}</Msg>
        <Section title="How to connect PostHog">
          <ol className="text-sm text-grayscale-1200 list-decimal ml-5 flex flex-col gap-1.5">
            <li>In PostHog, open <b>Settings → Personal API keys</b> and create a key with
              <b> Query read</b> access to this project.</li>
            <li>Add it to <code className="text-xs bg-grayscale-200 px-1 rounded">Backend/.env</code>:
              <pre className="text-xs bg-grayscale-200 rounded p-2 mt-1 overflow-x-auto">
POSTHOG_PERSONAL_API_KEY=phx_your_key_here
POSTHOG_PROJECT_ID=12345          # optional — discovered automatically
POSTHOG_HOST={config.data.host || 'https://us.posthog.com'}</pre>
            </li>
            <li>Restart the API, then reload this page.</li>
          </ol>
          <p className="text-xs text-grayscale-1100 mt-3">
            The key stays on the server — it is never sent to the browser. The write-only
            key the frontend already uses (<code className="text-xs">VITE_POSTHOG_KEY</code>)
            cannot read data back, which is why a second key is needed.
          </p>
        </Section>
        <Btn small onClick={config.reload}>Re-check</Btn>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {config.data?.project_error && <Msg kind="error">{config.data.project_error}</Msg>}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <SubTabs tabs={TABS} active={tab} onChange={setTab} />
        <label className="text-xs text-grayscale-1100 flex items-center gap-2">
          Last
          <Select value={days} onChange={setDays} options={RANGES} />
          days
        </label>
      </div>
      {tab === 'Overview' && <Overview days={days} />}
      {tab === 'Actions' && <Actions days={days} />}
      {tab === 'Funnels' && <Funnels days={days} />}
      {tab === 'People' && <People days={days} />}
      {tab === 'Query' && <QueryConsole />}
    </div>
  );
};

/** Handlers answer with `error` inside a 200 when PostHog itself complains. */
const Guard: React.FC<{ data: any; error?: string; children: React.ReactNode }> =
  ({ data, error, children }) => {
    if (error) return <Msg kind="error">{error}</Msg>;
    if (data?.error) return <Msg kind="error">{data.error}</Msg>;
    return <>{children}</>;
  };

const n = (v: any) => (typeof v === 'number' ? v.toLocaleString() : v ?? '—');

// ---------------------------------------------------------------------------
const Overview: React.FC<{ days: string }> = ({ days }) => {
  const summary = useAdminGet('posthog', 'summary', { days }, [days]);
  const series = useAdminGet('posthog', 'timeseries', { days }, [days]);
  const top = useAdminGet('posthog', 'top_actions', { days, limit: 10 }, [days]);
  const s = summary.data?.stats || {};

  return (
    <div className="flex flex-col gap-4">
      <Guard data={summary.data} error={summary.error}>
        {summary.loading ? <Loading /> : (
          <KPIRow>
            <KPI label="Events" value={n(s.events)} />
            <KPI label="People" value={n(s.people)} hint="distinct PostHog persons" />
            <KPI label="Distinct actions" value={n(s.distinct_actions)} />
            <KPI label="Active days" value={n(s.active_days)} hint={`of the last ${days}`} />
          </KPIRow>
        )}
      </Guard>

      <Section title="Daily volume" right={<Btn small onClick={series.reload}>Refresh</Btn>}>
        <Guard data={series.data} error={series.error}>
          {series.loading ? <Loading /> : (
            <DataTable rows={series.data?.rows || []} csvName="posthog_daily"
              columns={['day', 'events', 'people']}
              empty="No events in this window." />
          )}
        </Guard>
      </Section>

      <Section title="Busiest actions">
        <Guard data={top.data} error={top.error}>
          {top.loading ? <Loading /> : (
            <BarList items={(top.data?.rows || []).map((r: any) => ({
              label: r.action || '(unnamed)', value: Number(r.events || 0),
            }))} />
          )}
        </Guard>
      </Section>
    </div>
  );
};

// ---------------------------------------------------------------------------
const Actions: React.FC<{ days: string }> = ({ days }) => {
  const [selected, setSelected] = useState('');
  const actions = useAdminGet('posthog', 'top_actions', { days, limit: 200 }, [days]);
  const detail = useAdminGet('posthog', 'action_detail',
    { days, action: selected, limit: 25 }, [days, selected]);

  return (
    <div className="flex flex-col gap-4">
      <Section title="Every tracked action"
        right={<Btn small onClick={actions.reload}>Refresh</Btn>}>
        <p className="text-xs text-grayscale-1100 -mt-1">
          Use <b>Inspect</b> on a row to see that action's most recent events and the
          properties they carried.
        </p>
        <Guard data={actions.data} error={actions.error}>
          {actions.loading ? <Loading /> : (
            <DataTable rows={actions.data?.rows || []} searchable csvName="posthog_actions"
              columns={['action', 'events', 'people', 'last_seen']}
              actions={(r: any) => (
                <Btn small onClick={() => setSelected(r.action)}>Inspect</Btn>
              )}
              empty="Nothing recorded in this window." />
          )}
        </Guard>
      </Section>

      {selected && (
        <Section title={`Recent: ${selected}`}
          right={<Btn small onClick={() => setSelected('')}>Clear</Btn>}>
          <Guard data={detail.data} error={detail.error}>
            {detail.loading ? <Loading /> : (
              <DataTable rows={detail.data?.rows || []} csvName={`posthog_${selected}`}
                columns={['timestamp', 'person_id', 'properties']}
                empty="No events for this action in the window." />
            )}
          </Guard>
        </Section>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
const Funnels: React.FC<{ days: string }> = ({ days }) => {
  const funnels = useAdminGet('posthog', 'funnels', { days }, [days]);
  return (
    <div className="flex flex-col gap-4">
      <p className="text-xs text-grayscale-1100">
        Each step counts the distinct people who performed it in the window — not a
        strictly ordered funnel. Someone can hit the paywall on Monday and upgrade on
        Thursday, and an ordered funnel would drop them.
      </p>
      <Guard data={funnels.data} error={funnels.error}>
        {funnels.loading ? <Loading /> : (funnels.data?.funnels || []).map((f: any) => (
          <Section key={f.key} title={f.label}>
            <DataTable rows={f.steps || []} csvName={`funnel_${f.key}`}
              columns={['action', 'people', 'events', 'pct_of_first']}
              labels={{ pct_of_first: '% of step 1' }}
              empty="No data yet." />
          </Section>
        ))}
      </Guard>
      {funnels.data && !funnels.data.error && (funnels.data.funnels || []).length === 0 && (
        <Msg kind="error">No funnels configured.</Msg>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
const People: React.FC<{ days: string }> = ({ days }) => {
  const users = useAdminGet('posthog', 'top_users', { days, limit: 100 }, [days]);
  return (
    <Section title="Most active people" right={<Btn small onClick={users.reload}>Refresh</Btn>}>
      <p className="text-xs text-grayscale-1100 -mt-1">
        `person_id` is PostHog's identifier. The app calls identify() with our user id at
        login, so anyone who has signed in lines up with User 360; anonymous visitors
        keep a device-scoped id instead.
      </p>
      <Guard data={users.data} error={users.error}>
        {users.loading ? <Loading /> : (
          <DataTable rows={users.data?.rows || []} searchable csvName="posthog_people"
            columns={['person_id', 'events', 'distinct_actions', 'last_seen']}
            empty="No activity in this window." />
        )}
      </Guard>
    </Section>
  );
};

// ---------------------------------------------------------------------------
const QueryConsole: React.FC = () => {
  const [q, setQ] = useState(
    "SELECT properties.action AS action, count() AS events\n" +
    "FROM events\n" +
    "WHERE event = 'utech_click' AND timestamp >= now() - INTERVAL 7 DAY\n" +
    "GROUP BY action ORDER BY events DESC LIMIT 25");
  const [res, setRes] = useState<any>(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    const { data, error } = await panelPost('posthog', 'query', { query: q });
    setBusy(false);
    setRes(error ? { error } : data);
  };

  const rows = (res?.rows || []).map((row: any[]) =>
    Object.fromEntries((res?.columns || []).map((c: string, i: number) => [c, row[i]])));

  return (
    <Section title="HogQL console" right={<Btn small kind="primary" onClick={run}>Run</Btn>}>
      <p className="text-xs text-grayscale-1100 -mt-1">
        Read-only: SELECT / WITH only. PostHog's query API has no write statements, so the
        limit here is about staying inside the events dataset and returning a readable error.
      </p>
      <textarea value={q} onChange={e => setQ(e.target.value)} rows={7}
        spellCheck={false}
        className="w-full font-mono text-xs p-2.5 rounded-md border-[0.5px] border-primary-300" />
      {busy && <Loading label="Querying PostHog…" />}
      {res?.error && <Msg kind="error">{res.error}</Msg>}
      {res && !res.error && (
        <DataTable rows={rows} csvName="hogql_result"
          columns={res.columns || []} empty="Query returned no rows." />
      )}
    </Section>
  );
};

export default PostHogPanel;
